import {
  Button,
  ButtonGroupContext,
  type ButtonProps,
  CircularProgress,
} from '@mui/material'
import { useContext, useEffect, useState } from 'react'

type Props = {
  isLoading?: boolean
  isError?: boolean
  isSuccess?: boolean

  color?: ButtonProps['color']
  errorColor?: ButtonProps['color']
  successColor?: ButtonProps['color']

  resetTime?: number
  resetOnError?: boolean
} & ButtonProps

export const StatusButton = (props: Props) => {
  const {
    isLoading,
    isError,
    isSuccess,
    color,
    startIcon,
    errorColor = 'error',
    successColor = 'success',
    resetTime = 5000, //five seconds
    resetOnError = true,
    onClick,
    ...buttonProps
  } = props

  const buttonCtx = useContext(ButtonGroupContext)
  const originalColor = color ?? buttonCtx.color
  const [_color, setColor] = useState<ButtonProps['color']>(originalColor)
  const [_startIcon, setIcon] = useState<ButtonProps['startIcon']>(startIcon)
  const [interactionCount, setInteractionCount] = useState(0)

  const reset = () => {
    setColor(props.color)
  }

  const handleClick: ButtonProps['onClick'] = (event) => {
    setInteractionCount(interactionCount + 1)
    if (onClick) onClick(event)
  }

  useEffect(() => {
    if (isLoading) {
      setIcon(<CircularProgress size={18.5} color={_color ?? originalColor} />)
    } else {
      setIcon(startIcon)
    }
  }, [isLoading, startIcon, _color])

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = []

    if (isError) {
      setColor(errorColor)
      if (resetOnError) {
        timers.push(setTimeout(reset, resetTime))
      }
    }

    if (isSuccess) {
      setColor(successColor)
      timers.push(setTimeout(reset, resetTime))
    }

    return () => timers.forEach(clearTimeout)
  }, [isError, isSuccess, originalColor, interactionCount])

  return (
    <Button
      onClick={handleClick}
      startIcon={_startIcon}
      color={_color}
      {...buttonProps}
    />
  )
}
